'use client'

import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { isOverInterval } from '@/lib/time/services/time.helpers'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { trackingConsentAtom } from '@/lib/tracking/services/tracking.store'
import { useLazyQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { TrackFirstSignInDocument, type FirstLoginOriginAnalytics } from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useEffect, useRef } from 'react'
import { SIGNIN_TRACKING_INTERVAL } from '../../services/tracking.constants'
import {
  getSignInTrackingFromStorage,
  hasTrackedFirstLoginOrigin,
  saveFirstLoginOriginTrackedToStorage,
  saveSignInTrackingToStorage,
} from '../../services/tracking.unregister'

gql(/* GraphQL */ `
  query TrackFirstSignIn {
    currentUser {
      id
      firstLoginManagerOrigin
    }
  }
`)

const FIRST_LOGIN_ORIGIN_EVENT_LABEL: Record<FirstLoginOriginAnalytics, string> = {
  DOA_WEB: 'validation étape 3, confirmation mot de passe et doa finalisée',
  DOA_STORE: 'validation étape 3, confirmation mot de passe et doa magasin finalisée',
}

export function TrackFirstSignIn() {
  const isAuthenticated = useIsAuthenticated()
  const isTrackingEnabled = useAtomValue(trackingConsentAtom)
  const hasRequestedOrigin = useRef(hasTrackedFirstLoginOrigin())

  const [fetchOrigin, { data: firstSignInData }] = useLazyQuery(TrackFirstSignInDocument, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (!isAuthenticated || isSSR || !isTrackingEnabled) {
      return
    }

    const lastSignInTimestamp = getSignInTrackingFromStorage()
    const shouldPushAuth =
      !lastSignInTimestamp || isOverInterval(lastSignInTimestamp, SIGNIN_TRACKING_INTERVAL)

    if (shouldPushAuth) {
      pushToGtmDataLayer({
        event: 'gaEvent',
        eventCategory: 'parcours DOA DOC connexion',
        eventAction: 'connexion compte',
        eventLabel: 'authentification',
      })
      saveSignInTrackingToStorage(String(Date.now()))
    }
  }, [isAuthenticated, isTrackingEnabled])

  useEffect(() => {
    if (!isAuthenticated || !isTrackingEnabled || hasRequestedOrigin.current) {
      return
    }

    hasRequestedOrigin.current = true
    fetchOrigin()
  }, [isAuthenticated, isTrackingEnabled, fetchOrigin])

  useEffect(() => {
    if (!firstSignInData?.currentUser) {
      return
    }

    saveFirstLoginOriginTrackedToStorage()

    const doaOrigin = firstSignInData.currentUser.firstLoginManagerOrigin

    if (!doaOrigin) {
      return
    }

    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'parcours DOA DOC connexion',
      eventAction: 'doa',
      eventLabel: FIRST_LOGIN_ORIGIN_EVENT_LABEL[doaOrigin],
    })
  }, [firstSignInData])

  return null
}
